import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  Award, AwardMutationResponse, AwardPoints, AwardsResponse, ScoresResponse, Team, TeamScore,
} from '@mv/contracts';
import { AWARD_POINTS } from '@mv/contracts';
import Flag from '../components/Flag';
import { IconBack, IconClose, MarkTrophy } from '../components/Icon';
import { useSession } from '../lib/session';
import { ApiError, api } from '../lib/api';
import { fmtDay, fmtTime } from '../lib/time';

/**
 * The Scorecard (D56).
 *
 * **Standings for everyone; awarding for organisers.** The same split Featured
 * takes (D51) — what is organiser-only is changing the board, not reading it —
 * and the API enforces it, so this file only decides what to draw.
 *
 * A guest sees eight rows and nothing else. An organiser's rows are buttons:
 * tapping one opens a sheet carrying both halves of the job, the four amounts
 * and that team's existing awards with a remove beside each.
 *
 * **There is no second confirmation on the award, and that is deliberate.** The
 * team move has one (D54) because it is a native `<select>` where a mis-tap is
 * invisible and the only undo is knowing what the previous value was. Here the
 * sheet is already a deliberate second step, the amounts are full-width targets
 * rather than list rows, and every award can be removed from the same sheet
 * that made it. A confirmation on top of that is a speed bump, not a safeguard.
 */

/** Slow on purpose. The board moves a handful of times an evening, and this is
 *  a screen people leave open on a table. */
const POLL_MS = 30_000;

/**
 * The id travels with the request so the write is idempotent — see the note on
 * `Award.awardId`. Generated per tap rather than per render, so a retry after a
 * failure is a new award and a double-fire of one tap is not.
 */
function newAwardId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid.replace(/-/g, '');
  // Older WebViews. Length and alphabet are what the API checks.
  return Array.from({ length: 32 }, () => Math.floor(Math.random() * 36).toString(36)).join('');
}

export default function Scorecard() {
  const session = useSession();
  const navigate = useNavigate();
  const [standings, setStandings] = useState<TeamScore[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Team | null>(null);

  /**
   * Whether a board has ever loaded. A ref rather than derived from
   * `standings`, because the decision is made inside an async callback that
   * closed over an older render's value — and it must never be made inside a
   * state updater, which React is free to call twice.
   */
  const loaded = useRef(false);

  useEffect(() => {
    let live = true;
    const tick = async () => {
      // A background tab polling all evening is a request every thirty seconds
      // to paint nothing.
      if (document.visibilityState !== 'visible') return;
      try {
        const res = await api<ScoresResponse>('/scores');
        if (!live) return;
        loaded.current = true;
        setStandings(res.standings);
        setError(null);
      } catch {
        // Only the *first* load reports a failure. Once a board is on screen a
        // dropped poll should leave it there — replacing eight real rows with
        // an error because one request timed out on hotel wifi is a worse
        // answer than a board that is thirty seconds stale.
        if (live && !loaded.current) setError('That did not load. Try again in a moment.');
      }
    };
    void tick();
    const timer = window.setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      live = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, []);

  const isAdmin = session?.isAdmin ?? false;
  const total = standings?.reduce((n, s) => n + s.points, 0) ?? 0;

  return (
    <div className="page page-narrow page-narrow-lg" style={{ padding: '10px 20px 96px' }}>
      <button type="button" className="btn btn-secondary" style={{ minHeight: 42, gap: 8 }} onClick={() => navigate('/now')}>
        <IconBack size={16} /> Now
      </button>

      <h3 style={{ margin: '16px 0 4px' }}>Scorecard</h3>
      <p style={{ fontSize: 13, opacity: 0.7, margin: 0 }}>
        {isAdmin
          ? 'Tap a team to award points.'
          : 'Points your team has picked up over the week.'}
      </p>

      {error && <p style={{ fontSize: 13, color: 'var(--color-accent-700)', marginTop: 16 }} role="alert">{error}</p>}

      {!standings && !error && (
        <div className="stack" style={{ gap: 8, marginTop: 18 }}>
          {Array.from({ length: 8 }, (_, i) => <div key={i} className="sk sk-block" style={{ height: 54 }} />)}
        </div>
      )}

      {standings && total === 0 && (
        <div className="empty" style={{ marginTop: 22, marginBottom: 4 }}>
          <MarkTrophy className="mark" size={44} />
          <h4 style={{ margin: 0 }}>Nothing on the board yet</h4>
          <p>{isAdmin ? 'Tap a team below to start it off.' : 'Points appear here as they are handed out.'}</p>
        </div>
      )}

      {standings && (
        <div className="stack" style={{ gap: 8, marginTop: 18 }}>
          {standings.map((s, i) => (
            <ScoreRow
              key={s.team}
              score={s}
              /* Rank is only meaningful once somebody is ahead. While every
                 team is on nothing, numbering them 1–8 invents a standing. */
              rank={total === 0 ? null : i + 1}
              mine={s.team === session?.team}
              onOpen={isAdmin ? () => setOpen(s.team) : undefined}
            />
          ))}
        </div>
      )}

      {open && (
        <AwardSheet
          team={open}
          points={standings?.find((s) => s.team === open)?.points ?? 0}
          onClose={() => setOpen(null)}
          onStandings={setStandings}
        />
      )}
    </div>
  );
}

function ScoreRow({ score, rank, mine, onOpen }: {
  score: TeamScore; rank: number | null; mine: boolean; onOpen?: () => void;
}) {
  const body = (
    <>
      <span className="score-rank" aria-hidden={rank === null}>{rank ?? '·'}</span>
      <Flag team={score.team} height={20} decorative />
      <span className="score-team">
        {score.team}
        {/* Worth marking: on a board of eight this is the row a guest is
            actually looking for. */}
        {mine && <span className="tag tag-accent-2" style={{ fontSize: 10 }}>You</span>}
      </span>
      <span className="score-points">{score.points.toLocaleString()}</span>
    </>
  );

  // A guest's row is not a button. Rendering one that does nothing when tapped
  // is worse than rendering none.
  if (!onOpen) return <div className="card score-row">{body}</div>;
  return (
    <button type="button" className="card score-row score-row-btn" onClick={onOpen}>
      {body}
    </button>
  );
}

/**
 * The organiser's half: award, and undo.
 *
 * Both live in one sheet because they are one job — the reason the ledger
 * exists at all is that a mis-tapped 500 has to be removable, and putting the
 * undo two screens away from the mistake would waste that.
 *
 * The ledger is fetched when the sheet opens rather than with the board, so a
 * guest never receives a list they cannot render. The API refuses it to them
 * anyway; this is the client not asking.
 */
function AwardSheet({ team, points, onClose, onStandings }: {
  team: Team;
  points: number;
  onClose: () => void;
  onStandings: (s: TeamScore[]) => void;
}) {
  const [awards, setAwards] = useState<Award[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAwards = useCallback(async () => {
    const res = await api<AwardsResponse>('/admin/scores');
    // Newest first: the award most likely to need removing is the one just made.
    setAwards(res.awards.filter((a) => a.team === team).reverse());
  }, [team]);

  useEffect(() => {
    let live = true;
    // An empty list rather than an error: the sheet's job is awarding, and a
    // ledger that failed to load must not stand between an organiser and the
    // four buttons above it.
    loadAwards().catch(() => { if (live) setAwards([]); });
    return () => { live = false; };
  }, [loadAwards]);

  async function run(work: () => Promise<AwardMutationResponse>) {
    setBusy(true);
    setError(null);
    try {
      const res = await work();
      onStandings(res.standings);
      await loadAwards();
    } catch (err) {
      // The sheet stays open with the message in it, so the organiser can
      // simply press again — and the error renders *inside* the scrim, because
      // a page-level banner behind a `position: fixed` overlay is invisible.
      setError(err instanceof ApiError ? err.message : 'That did not save. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sheet-scrim" role="dialog" aria-modal="true" aria-label={`Award points to ${team}`}>
      <div className="sheet">
        <button type="button" className="sheet-close" onClick={onClose} aria-label="Close">
          <IconClose size={16} />
        </button>

        <h4 style={{ margin: '0 0 2px' }}>
          <Flag team={team} height={20} decorative /> {team}
        </h4>
        <div style={{ fontFamily: 'var(--font-heading)', fontSize: 30 }}>
          {points.toLocaleString()} <span style={{ fontSize: 14, opacity: 0.6 }}>points</span>
        </div>

        <div className="award-buttons">
          {AWARD_POINTS.map((p) => (
            <button
              key={p}
              type="button"
              className="btn btn-primary award-btn"
              disabled={busy}
              onClick={() => run(() => api<AwardMutationResponse>('/admin/scores', {
                method: 'POST',
                json: { awardId: newAwardId(), team, points: p as AwardPoints },
              }))}
            >
              +{p}
            </button>
          ))}
        </div>

        {error && (
          <p role="alert" style={{ fontSize: 13, color: 'var(--color-accent-700)', margin: '14px 0 0', fontWeight: 600 }}>
            {error}
          </p>
        )}

        {awards && awards.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', opacity: 0.6, marginBottom: 6 }}>
              Awarded
            </div>
            <div className="stack" style={{ gap: 6 }}>
              {awards.map((a) => (
                <div key={a.awardId} className="award-row">
                  <span className="award-pts">+{a.points}</span>
                  <span className="award-when">{fmtDay(a.awardedAt)} {fmtTime(a.awardedAt)}</span>
                  <button
                    type="button"
                    className="btn btn-secondary award-undo"
                    disabled={busy}
                    aria-label={`Remove ${a.points} points awarded at ${fmtTime(a.awardedAt)}`}
                    onClick={() => run(() => api<AwardMutationResponse>(`/admin/scores/${a.awardId}`, { method: 'DELETE' }))}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {awards && awards.length === 0 && (
          <p style={{ fontSize: 13, opacity: 0.7, marginTop: 18 }}>
            Nothing awarded to {team} yet.
          </p>
        )}

        <button type="button" className="btn btn-secondary" style={{ width: '100%', marginTop: 18 }} onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}
